import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import emailjs from '@emailjs/browser';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    hospitalSize: '',
    message: '',
    interestedIn: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [errors, setErrors] = useState({});
  const [visibleElements, setVisibleElements] = useState([]);
  const sectionRef = useRef(null);
  const formRef = useRef(null);

  const contactInfo = [
    {
      icon: '📞',
      title: 'Phone Support',
      content: '8287999236',
      link: 'tel:+918287999236',
      description: '24/7 technical support available'
    },
    {
      icon: '✉️',
      title: 'Email Support',
      content: 'wayugpl@gmail.com',
      link: 'mailto:wayugpl@gmail.com',
      description: 'Get help within 2 hours'
    },
    {
      icon: '💬',
      title: 'Live Chat',
      content: 'Start Chat',
      link: '#',
      description: 'Instant help from our team'
    },
    {
      icon: '📍',
      title: 'Office Address',
      content: 'Connaught Place, New Delhi, Delhi, India',
      link: '#',
      description: 'Head Office'
    }
  ];

  const hospitalSizes = [
    'Small Clinic (1-25 beds)',
    'Medium Hospital (26-100 beds)',
    'Large Hospital (101-300 beds)',
    'Health System (300+ beds)',
    'Multi-location Network'
  ];

  const interestedInOptions = [
    'Patient Management System',
    'Clinical Workflows',
    'Financial Management',
    'Analytics & Reporting',
    'Full Platform Demo',
    'Custom Integration'
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.dataset.element;
            setVisibleElements(prev => [...prev, elementId]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = sectionRef.current?.querySelectorAll('[data-element]');
    elements?.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company/Hospital name is required';
    }

    if (formData.phone && !/^[\d\s+()\-]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);

    try {
      // EmailJS configuration
      const templateParams = {
        // to_name: 'Wayug Team',
        title:formData.interestedIn + ' - ' + formData.interestedIn,
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        // phone: formData.phone,
        // company: formData.company,
        // job_title: formData.jobTitle,
        // hospital_size: formData.hospitalSize,
        // interested_in: formData.interestedIn,
        message: formData.message +
        '<br><br>Phone: ' + formData.phone +
        '<br>Company: ' + formData.company +
        '<br>Job Title: ' + formData.jobTitle +
        '<br>Hospital Size: ' + formData.hospitalSize +
        '<br>Interested In: ' + formData.interestedIn,
              // reply_to: formData.email
      };

      await emailjs.send(
        'service_40xxtug',        // Replace with your EmailJS service ID
        'template_bmokd3i',       // Replace with your EmailJS template ID
        templateParams,
        'WriALeXYrvgPDfpBx'         // Replace with your EmailJS public key
      );

      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        jobTitle: '',
        hospitalSize: '',
        message: '',
        interestedIn: ''
      });

      // Clear success message after 5 seconds
      setTimeout(() => setSubmitStatus(''), 5000);

    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact" ref={sectionRef}>
      <Helmet>
        <title>Contact — Mediway Nextgen HMS India</title>
        <meta name="description" content="Contact Mediway Nextgen for demos, pricing, and enquiries about our Hospital Management System (HMS) for Indian hospitals." />
        <link rel="canonical" href="https://mediwaynextgen.wayug.com/#contact" />
      </Helmet>
      <div className="container">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          'name': 'Mediway Nextgen (Wayug Infotech Solutions Limited)',
          'url': 'https://mediwaynextgen.wayug.com',
          'telephone': '8287999236',
          'email': 'wayugpl@gmail.com',
          'address': {
            '@type': 'PostalAddress',
            'streetAddress': 'Connaught Place',
            'addressLocality': 'New Delhi',
            'addressRegion': 'Delhi',
            'addressCountry': 'IN'
          },
          'sameAs': ['https://linkedin.com/company/wayug', 'https://twitter.com/wayug', 'https://facebook.com/wayug']
        }) }} />

        <div 
          className={`section-header ${visibleElements.includes('header') ? 'section-header--visible' : ''}`}
          data-element="header"
        >
          <h1 className="section-title">
            Get Started with Mediway Nextgen HMS Software
          </h1>
          <p className="section-subtitle">
            Ready to transform your healthcare operations? Contact Mediway Nextgen for a personalized demo 
            and see how our Hospital Management System can benefit your facility. We offer the best HMS Software Pricing.
          </p>
        </div>

        <div className="contact-content">
          {/* Contact Information */}
          <div 
            className={`contact-info ${visibleElements.includes('info') ? 'contact-info--visible' : ''}`}
            data-element="info"
          >
            <div className="info-header">
              <h3>Get in Touch</h3>
              <p>Multiple ways to reach our team</p>
            </div>

            <div className="contact-methods">
              {contactInfo.map((item, index) => (
                <div key={index} className="contact-method">
                  <div className="method-icon">
                    <span>{item.icon}</span>
                  </div>
                  <div className="method-content">
                    <h4>{item.title}</h4>
                    {item.link !== '#' ? (
                      <a href={item.link} className="method-link" rel="nofollow">
                        {item.content}
                      </a>
                    ) : (
                      <span className="method-text">{item.content}</span>
                    )}
                    <p className="method-description">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="response-time">
              <div className="response-badge">
                <div className="badge-icon">⚡</div>
                <div className="badge-content">
                  <strong>Quick Response</strong>
                  <span>We respond within 2 hours during business hours</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div 
            className={`contact-form-container ${visibleElements.includes('form') ? 'contact-form-container--visible' : ''}`}
            data-element="form"
          >
            {submitStatus === 'success' && (
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h3>Thank You!</h3>
                <p>Your message has been sent successfully. Our team will contact you within 2 hours during business hours.</p>
              </div>
            )}

            <form ref={formRef} className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="form-header">
                <h3>Request a Demo</h3>
                <p>Fill out the form below and we'll get back to you shortly</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? 'error' : ''}
                    disabled={isSubmitting}
                    required
                  />
                  {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? 'error' : ''}
                    disabled={isSubmitting}
                    required
                  />
                  {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Business Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                    disabled={isSubmitting}
                    required
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder="8287999236"
                    disabled={isSubmitting}
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Company/Hospital Name *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={errors.company ? 'error' : ''}
                    disabled={isSubmitting}
                    required
                  />
                  {errors.company && <span className="error-text">{errors.company}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="jobTitle">Job Title</label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="e.g., CIO, Medical Director"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="hospitalSize">Hospital Size</label>
                  <select
                    id="hospitalSize"
                    name="hospitalSize"
                    value={formData.hospitalSize}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Select hospital size</option>
                    {hospitalSizes.map((size, index) => (
                      <option key={index} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="interestedIn">Most Interested In</label>
                  <select
                    id="interestedIn"
                    name="interestedIn"
                    value={formData.interestedIn}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Select primary interest</option>
                    {interestedInOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Additional Information</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Tell us about your specific needs, current challenges, or questions about Mediway Nextgen HMS..."
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={`btn btn-primary submit-btn ${isSubmitting ? 'loading' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Sending Request...
                    </>
                  ) : (
                    <>
                      Request Demo
                      <svg className="btn-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>

                <p className="form-note">
                  By submitting this form, you agree to receive communications from Mediway Nextgen Infotech Solutions Limited. 
                  We respect your privacy and will never share your information.
                </p>
              </div>

              {submitStatus === 'error' && (
                <div className="error-message">
                  <span className="error-icon">❌</span>
                  <span>There was an error sending your message. Please try again or contact us directly.</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <div className="contact-background">
        <div className="background-pattern"></div>
      </div>
    </section>
  );
};

export default Contact;